import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';

import { PageHeader } from '../../../components/common/PageHeader';
import { Table, type IColumn } from '../../../components/common/Table';
import { routes } from '../../../lib/constants';
import { formatRelative } from '../../../lib/format';
import {
  operationalSourceLabel,
  sourceDisplayLabel,
  useMetaphysicsPipelines,
  type IEvalDataset,
  type IPipeline,
} from '../../../lib/metaphysics-pipelines';
import { guardSuperuser } from '../index';

export const Route = createFileRoute('/internal/pipelines/')({
  beforeLoad: () => guardSuperuser(),
  component: PipelinesListPage,
});

function summarizeSources(
  p: IPipeline,
  allPipelines: IPipeline[] | undefined,
  datasets: IEvalDataset[] | undefined,
): string {
  // Resolve source labels for the row. Knowledge sources come hydrated from
  // the server only sometimes; fall back to the bare sourceId snippet.
  const ks = (p.knowledgeSources ?? [])
    .map((e) => sourceDisplayLabel(e.source))
    .filter((s) => s !== 'Unknown');
  const ops = (p.operationalSources ?? []).map((s) =>
    operationalSourceLabel(s, datasets ?? null, allPipelines ?? null),
  );
  const all = [...ks, ...ops];
  return all.length === 0 ? '—' : all.join(' · ');
}

function PipelinesListPage() {
  const mp = useMetaphysicsPipelines();
  const qc = useQueryClient();
  const nav = useNavigate();

  const list = useQuery({
    queryKey: ['mp', 'pipelines'],
    queryFn: () => mp.listPipelines(),
  });
  // Datasets are needed so eval-based operational sources render with
  // friendly names instead of UUID prefixes in the row summary.
  const datasets = useQuery({
    queryKey: ['mp', 'datasets'],
    queryFn: () => mp.listDatasets(),
  });

  const remove = useMutation({
    mutationFn: (id: string) => mp.deletePipeline(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mp', 'pipelines'] }),
  });

  const cols: IColumn<IPipeline>[] = [
    {
      key: 'name',
      header: 'Name',
      cell: (p) => (
        <Link
          to={routes.internalPipelineDetail(p.id)}
          style={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-med)' }}
        >
          {p.name}
        </Link>
      ),
    },
    {
      key: 'blueprints',
      header: 'Blueprints',
      width: 110,
      cell: (p) => <span className="badge badge--neutral">{p.blueprints?.length ?? 0}</span>,
    },
    {
      key: 'sources',
      header: 'Sources',
      cell: (p) => (
        <span className="t-secondary">{summarizeSources(p, list.data, datasets.data)}</span>
      ),
    },
    {
      key: 'created',
      header: 'Created',
      mono: true,
      width: 140,
      cell: (p) => formatRelative(p.createdAt),
    },
    {
      key: 'actions',
      header: '',
      width: 80,
      cell: (p) => (
        <button
          type="button"
          className="btn btn--ghost btn--xs"
          onClick={(e) => {
            e.stopPropagation();
            if (confirm(`Delete pipeline "${p.name}"? This is irreversible.`)) remove.mutate(p.id);
          }}
          aria-label={`Delete ${p.name}`}
        >
          ✕
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Pipelines"
        actions={
          <Link to={routes.INTERNAL_PIPELINES_NEW} className="btn btn--primary btn--sm">
            New pipeline
          </Link>
        }
      />
      <Table<IPipeline>
        rows={list.data}
        columns={cols}
        rowKey={(p) => p.id}
        isLoading={list.isLoading}
        error={list.error as Error | null}
        emptyTitle="No pipelines yet"
        emptyDescription="A pipeline is the unit of execution: it pulls from knowledge / operational sources, runs blueprint prompts, and emits artifacts to a versioned knowledge graph."
        emptyCta={
          <Link to={routes.INTERNAL_PIPELINES_NEW} className="btn btn--primary btn--sm">
            New pipeline
          </Link>
        }
        onRowClick={async (p) => {
          try {
            await nav({ to: routes.internalPipelineDetail(p.id) });
          } catch (err) {
            console.error(err);
          }
        }}
      />
    </>
  );
}
